import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import Template from "ui/Template";
import Form from "ui/elements/form/Form";
import Utils from "ui/helpers/Utils";
import scenePropsView from 'ui/editor/dialogs/scene-props/scene-props.handlebars';
import {validateSceneName} from "ui/helpers/validators";

/**
 * @type {Template}
 */
let template = null;

/**
 * Scenes Library
 */
export default class SceneProps extends Dialog {
	/**
	 * Class Constructor
	 *
	 * @param {T_ScenePropsOptions} [options]
	 */
	constructor(options = {}) {
		super({
			title: i18n('title.scene-props'),
			buttons: [
				{
					caption: i18n('buttons.apply'),
					role: 'apply',
					icon: '/images/icons.svg#icon-svg-check'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		if (template === null) {
			template = new Template(scenePropsView);
		}

		/**
		 * @type {T_ScenePropsOptions}
		 * @private
		 */
		this._options = options;

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(data:boolean)}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm();
	}

	/**
	 * Shows note text which asks to fill out scene name
	 *
	 * @returns {SceneProps}
	 */
	highlightEmptySceneName() {
		if (!this._form) {
			return this;
		}
		this._form.indicateField(i18n('inputs.scene.note-name'), 'name');
		return this;
	}

	/**
	 * Shows note text which asks to fill out NEW scene name
	 *
	 * @returns {SceneProps}
	 */
	highlightNewSceneName() {
		if (!this._form) {
			return this;
		}
		this._form.indicateField(i18n('inputs.scene.note-new-name'), 'name');
		return this;
	}

	/**
	 * Returns promise which will be resolved in any case with one of two values:
	 * 1. Object - if form is ok and submitted;
	 * 2. null - if form has been closed;
	 *
	 * @returns {Promise<Object|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * Handles click by button
	 *
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);
		if (role === 'apply') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data);
			}
		}
	}

	/**
	 * Applies form data and destroy Dialog Box if all ok
	 *
	 * @private
	 * @param {Object} data
	 * @returns {void}
	 */
	_apply(data) {
		if (this._options.canvas) {
			this._options.canvas.updateProps({
				name: data.name,
				desc: data.desc,
			});
		}
		if (this._onComplete) {
			this._onComplete(true);
			this._onComplete = null;
		}
		this.destroy();
	}

	/**
	 * Builds scene properties form
	 *
	 * @private
	 * @returns {void}
	 */
	async _buildForm() {
		let el = template.createElement({});
		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: this._options.canvas ? this._options.canvas.getProps() : null,
			fields: [
				{
					type: 'text',
					name: 'name',
					caption: i18n('inputs.scene.name'),
					fullWidth: true,
					required: true,
					maxlength: 64,
					validators: [validateSceneName]
				},
				{
					type: 'textarea',
					name: 'desc',
					caption: i18n('inputs.scene.desc'),
					maxlength: 2048,
				},
			]
		});
		this.setContent(el);
		// focus first input
		await Utils.wait(40);
		el.querySelector('input').focus();
	}

	/**
	 * Destroys the dialog box
	 *
	 * @returns {void}
	 */
	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(false);
			this._onComplete = null;
		}
		super.destroy();
	}
}
